import { FadeIn } from './FadeIn';
import { NavBar } from './NavBar';
import { useLanguage } from './LanguageContext';

const pillarVisuals = [
  {
    icon: 'live_tv',
    image:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'stadium',
    image:
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'account_balance',
    image:
      'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=1200&q=80',
  },
];

const valueIcons = ['verified', 'lightbulb', 'handshake', 'visibility'];

const specializationVisuals = [
  {
    icon: 'gavel',
    image:
      'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'calculate',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'trending_up',
    image:
      'https://images.unsplash.com/photo-1559526324-593bc073d938?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'campaign',
    image:
      'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'analytics',
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
  },
  {
    icon: 'flight_takeoff',
    image:
      'https://images.unsplash.com/photo-1529074963764-98f45c47344b?auto=format&fit=crop&w=1200&q=80',
  },
];

const technologyIcons = ['hub', 'analytics', 'language'];
const partnershipIcons = ['theater_comedy', 'sync_alt', 'public'];
const objectiveIcons = ['shield', 'cloud_sync', 'travel_explore', 'bolt', 'diversity_3'];

export function AboutPage() {
  const { messages, t, language, toggleLanguage } = useLanguage();
  const aboutPage = messages.aboutPage;

  return (
    <>
      <NavBar t={t} language={language} toggleLanguage={toggleLanguage} />
      <main className="bg-surface-container-lowest min-h-screen pb-20">
        {/* Hero */}
        <section className="relative isolate overflow-hidden bg-primary">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2400&q=80"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/80 to-primary" />
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-secondary/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-8 pt-40 pb-24 lg:pb-32">
            <FadeIn>
              <div className="max-w-4xl space-y-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white/85 text-sm font-semibold uppercase tracking-[0.25em]">
                  <span className="material-symbols-outlined text-secondary text-base" aria-hidden="true">
                    auto_awesome
                  </span>
                  {aboutPage.introTagline}
                </span>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-headline font-extrabold text-white leading-[1.05] tracking-tight">
                  {aboutPage.title}
                </h1>
                <p className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl">
                  {aboutPage.intro}
                </p>
              </div>
            </FadeIn>
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-8 pb-16 lg:pb-24">
            <FadeIn delay={120}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { k: '50+', v: language === 'ar' ? 'دولة مستهدفة' : 'Target countries' },
                  { k: '3', v: language === 'ar' ? 'مراكز إقليمية' : 'Regional hubs' },
                  { k: '6', v: language === 'ar' ? 'تخصصات رئيسية' : 'Core specializations' },
                  { k: '24/7', v: language === 'ar' ? 'استجابة مستمرة' : 'Continuous response' },
                ].map((s) => (
                  <div
                    key={s.v}
                    className="rounded-2xl bg-white/5 backdrop-blur-md border border-white/15 p-5"
                  >
                    <div className="text-3xl sm:text-4xl font-headline font-extrabold text-secondary">
                      {s.k}
                    </div>
                    <div className="mt-1 text-white/75 text-sm font-semibold">{s.v}</div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-2 relative">
                <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-2xl shadow-primary/20">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80"
                    alt={aboutPage.visionMessage.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 rtl:-left-6 rtl:right-auto w-32 h-32 bg-secondary rounded-3xl flex items-center justify-center shadow-xl shadow-secondary/40">
                  <span className="material-symbols-outlined text-white text-6xl" aria-hidden="true">
                    rocket_launch
                  </span>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-8">
                <div className="space-y-3">
                  <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                    {language === 'ar' ? 'من نحن' : 'About us'}
                  </span>
                  <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                    {aboutPage.visionMessage.title}
                  </h2>
                </div>
                <div className="space-y-5">
                  {aboutPage.visionMessage.paragraphs.map((paragraph, idx) => (
                    <div
                      key={idx}
                      className="relative pl-6 rtl:pr-6 rtl:pl-0 border-l-4 rtl:border-r-4 rtl:border-l-0 border-secondary/70"
                    >
                      <p className="text-on-surface-variant leading-relaxed text-lg">
                        {paragraph}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* Pillars */}
        <section className="bg-primary relative overflow-hidden py-20 sm:py-28">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary via-primary/90 to-primary" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-8">
            <FadeIn>
              <div className="mb-14 space-y-4">
                <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                  {language === 'ar' ? 'ما يميزنا' : 'What sets us apart'}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-extrabold text-white tracking-tight whitespace-nowrap no-balance">
                  {aboutPage.pillars.title}
                </h2>
                <p className="text-white/75 text-lg leading-relaxed max-w-3xl">{aboutPage.pillars.intro}</p>
              </div>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutPage.pillars.items.map((item, idx) => {
                const visual = pillarVisuals[idx] || pillarVisuals[0];
                return (
                  <FadeIn key={item.title} delay={idx * 80}>
                    <article className="group relative h-full rounded-3xl overflow-hidden border border-white/10 hover:border-secondary/60 transition-colors">
                      <img
                        src={visual.image}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/90 to-primary/30" />
                      <div className="relative p-8 flex flex-col h-full min-h-[340px]">
                        <div className="w-14 h-14 rounded-2xl bg-secondary/20 border border-secondary/40 flex items-center justify-center mb-6">
                          <span
                            className="material-symbols-outlined text-secondary text-3xl"
                            aria-hidden="true"
                          >
                            {visual.icon}
                          </span>
                        </div>
                        <span className="text-secondary/80 font-bold text-sm tracking-widest">
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                        <h3 className="mt-2 text-2xl font-headline font-bold text-white">
                          {item.title}
                        </h3>
                        <p className="mt-4 text-white/80 leading-relaxed">{item.body}</p>
                      </div>
                    </article>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto space-y-4 mb-14">
              <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                {language === 'ar' ? 'مبادئنا' : 'Our principles'}
              </span>
              <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                {aboutPage.values.title}
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {aboutPage.values.items.map((item, idx) => (
              <FadeIn key={item.title} delay={idx * 60}>
                <article className="h-full rounded-3xl bg-gradient-to-br from-surface to-surface-container p-7 border border-outline-variant/20 hover:border-secondary/50 hover:shadow-xl hover:shadow-secondary/10 transition-all duration-300">
                  <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-5 shadow-lg shadow-primary/20">
                    <span
                      className="material-symbols-outlined text-secondary text-3xl"
                      aria-hidden="true"
                    >
                      {valueIcons[idx] || 'star'}
                    </span>
                  </div>
                  <h3 className="text-xl font-headline font-bold text-primary mb-3">{item.title}</h3>
                  <p className="text-on-surface-variant leading-relaxed">{item.body}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* Specializations */}
        <section className="bg-surface-container py-20 sm:py-28">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-8">
            <FadeIn>
              <div className="max-w-3xl mb-14 space-y-4">
                <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                  {language === 'ar' ? 'خبراتنا' : 'Our expertise'}
                </span>
                <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                  {aboutPage.specializations.title}
                </h2>
                <p className="text-on-surface-variant text-lg leading-relaxed">
                  {aboutPage.specializations.intro}
                </p>
              </div>
            </FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {aboutPage.specializations.items.map((item, idx) => {
                const visual = specializationVisuals[idx] || specializationVisuals[0];
                return (
                  <FadeIn key={item.title} delay={idx * 60}>
                    <article className="group h-full rounded-3xl overflow-hidden bg-surface-container-lowest border border-outline-variant/20 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1 transition-all duration-500">
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={visual.image}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/20 to-transparent" />
                        <div className="absolute top-4 left-4 rtl:right-4 rtl:left-auto">
                          <span className="text-white/70 font-bold text-sm tracking-widest">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <div className="absolute -bottom-6 right-6 rtl:left-6 rtl:right-auto w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center shadow-lg shadow-secondary/40 border-4 border-surface-container-lowest">
                          <span
                            className="material-symbols-outlined text-white text-2xl"
                            aria-hidden="true"
                          >
                            {visual.icon}
                          </span>
                        </div>
                      </div>
                      <div className="p-7 pt-8">
                        <h3 className="text-xl font-headline font-bold text-primary mb-3">
                          {item.title}
                        </h3>
                        <p className="text-on-surface-variant leading-relaxed">{item.body}</p>
                      </div>
                    </article>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>

        {/* Technology */}
        <section className="relative isolate overflow-hidden py-20 sm:py-28 bg-primary">
          <div className="absolute inset-0 opacity-30">
            <img
              src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=2000&q=80"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
          </div>
          <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-8">
            <FadeIn>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-14">
                <div className="space-y-5">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/15 border border-secondary/40 text-secondary text-sm font-semibold uppercase tracking-[0.2em]">
                    <span className="material-symbols-outlined text-base" aria-hidden="true">
                      smart_toy
                    </span>
                    {language === 'ar' ? 'تقنية مدعومة بالذكاء' : 'AI powered'}
                  </span>
                  <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-white tracking-tight">
                    {aboutPage.technology.title}
                  </h2>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {aboutPage.technology.intro}
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 bg-secondary/20 blur-3xl rounded-full" />
                  <img
                    src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80"
                    alt={aboutPage.technology.title}
                    className="relative w-full h-[340px] object-cover rounded-3xl border-[6px] border-white/10 shadow-2xl"
                  />
                </div>
              </div>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutPage.technology.items.map((item, idx) => (
                <FadeIn key={item.title} delay={idx * 80}>
                  <article className="h-full rounded-3xl bg-white/5 backdrop-blur-sm p-7 border border-white/10 hover:bg-white/10 hover:border-secondary/40 transition-all">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                        <span
                          className="material-symbols-outlined text-white text-2xl"
                          aria-hidden="true"
                        >
                          {technologyIcons[idx] || 'memory'}
                        </span>
                      </div>
                      <h3 className="text-xl font-headline font-bold text-white">{item.title}</h3>
                    </div>
                    <p className="text-white/75 leading-relaxed">{item.body}</p>
                  </article>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Partnership */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <FadeIn className="lg:col-span-2 order-last lg:order-first">
              <div className="relative">
                <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-2xl shadow-primary/20">
                  <img
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80"
                    alt={aboutPage.partnership.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -top-6 -left-6 rtl:-right-6 rtl:left-auto w-32 h-32 bg-primary rounded-3xl flex items-center justify-center shadow-xl">
                  <span className="material-symbols-outlined text-secondary text-6xl" aria-hidden="true">
                    handshake
                  </span>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={100} className="lg:col-span-3 space-y-8">
              <div className="space-y-4">
                <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                  {language === 'ar' ? 'كن شريكًا' : 'Become a partner'}
                </span>
                <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                  {aboutPage.partnership.title}
                </h2>
                <p className="text-on-surface-variant text-lg leading-relaxed">
                  {aboutPage.partnership.intro}
                </p>
              </div>
              <ul className="space-y-4">
                {aboutPage.partnership.items.map((item, idx) => (
                  <li
                    key={item.title}
                    className="group flex gap-5 rounded-2xl bg-surface-container p-6 border border-outline-variant/20 hover:border-secondary/40 hover:shadow-lg transition-all"
                  >
                    <div className="shrink-0 w-14 h-14 rounded-2xl bg-primary flex items-center justify-center group-hover:bg-secondary transition-colors">
                      <span
                        className="material-symbols-outlined text-secondary group-hover:text-white text-3xl transition-colors"
                        aria-hidden="true"
                      >
                        {partnershipIcons[idx] || 'check_circle'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold text-primary mb-1">
                        {item.title}
                      </h3>
                      <p className="text-on-surface-variant leading-relaxed">{item.body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </section>

        {/* Objectives */}
        <section className="bg-surface-container py-20 sm:py-28">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-8">
            <FadeIn>
              <div className="text-center max-w-3xl mx-auto space-y-4 mb-14">
                <span className="text-base sm:text-lg font-bold text-secondary uppercase tracking-[0.3em]">
                  {language === 'ar' ? 'رؤيتنا التنفيذية' : 'Our strategy'}
                </span>
                <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-primary tracking-tight">
                  {aboutPage.objectives.title}
                </h2>
              </div>
            </FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutPage.objectives.items.map((item, idx) => (
                <FadeIn key={item.title} delay={idx * 60}>
                  <article className="relative h-full rounded-3xl bg-surface-container-lowest p-7 border border-outline-variant/20 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="absolute top-0 right-0 rtl:left-0 rtl:right-auto w-24 h-24 bg-secondary/10 rounded-bl-[3rem] rtl:rounded-br-[3rem] rtl:rounded-bl-none flex items-start justify-end rtl:justify-start p-4">
                      <span className="text-3xl font-headline font-extrabold text-secondary/80">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-5 mt-2">
                      <span
                        className="material-symbols-outlined text-secondary text-3xl"
                        aria-hidden="true"
                      >
                        {objectiveIcons[idx] || 'flag'}
                      </span>
                    </div>
                    <h3 className="text-xl font-headline font-bold text-primary mb-3 pr-10 rtl:pl-10 rtl:pr-0">
                      {item.title}
                    </h3>
                    <p className="text-on-surface-variant leading-relaxed">{item.body}</p>
                  </article>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
          <FadeIn>
            <div className="relative rounded-[2.5rem] overflow-hidden isolate">
              <img
                src="https://images.unsplash.com/photo-1496307653780-42ee777d4833?auto=format&fit=crop&w=2000&q=80"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/70" />
              <div className="relative px-8 sm:px-16 py-16 sm:py-24 text-center max-w-4xl mx-auto space-y-6">
                <span
                  className="material-symbols-outlined text-secondary text-6xl"
                  aria-hidden="true"
                >
                  auto_awesome
                </span>
                <h2 className="text-4xl sm:text-5xl font-headline font-extrabold text-white tracking-tight">
                  {aboutPage.closing.title}
                </h2>
                <p className="text-white/85 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
                  {aboutPage.closing.body}
                </p>
                <div className="pt-4 flex flex-wrap gap-4 justify-center">
                  <a
                    href="/#registration"
                    className="px-8 py-4 bg-secondary text-white rounded-xl font-bold text-lg hover:shadow-[0_0_40px_-10px_rgba(119,90,25,0.6)] transition-all"
                  >
                    {language === 'ar' ? 'انضم إلينا' : 'Join us'}
                  </a>
                  <a
                    href="/"
                    className="px-8 py-4 bg-white/10 border border-white/25 text-white rounded-xl font-bold text-lg hover:bg-white/15 transition-all backdrop-blur-md"
                  >
                    {language === 'ar' ? 'العودة إلى الرئيسية' : 'Back to home'}
                  </a>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>
      </main>
    </>
  );
}
